import { useCallback, useRef } from "react";

/**
 * Phase 31 — Touch-friendly drag fallback for HTML5 D&D.
 *
 * Returns the props you need to spread on a draggable element. Works with
 * mouse (HTML5 D&D) AND touch (iPad/tablet) by translating touchmove → drop
 * via document.elementFromPoint().
 *
 * Usage:
 *   const dragProps = useTouchDraggable({ index, onReorder, dragType: "item" });
 *   <div {...dragProps} ... />
 *
 * onReorder(fromIndex, toIndex) is invoked when the touch drop lands on
 * another draggable element with the same data-drag-type.
 */
export default function useTouchDraggable({ index, onReorder, dragType, onStart, onEnd }) {
  const startY = useRef(null);
  const startX = useRef(null);
  const moved = useRef(false);

  const findTargetIndex = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    // Walk up to find a draggable ancestor with matching data-drag-type
    let cur = el;
    while (cur && cur !== document.body) {
      if (cur.dataset && cur.dataset.dragType === dragType && cur.dataset.dragIndex != null) {
        const idx = Number(cur.dataset.dragIndex);
        return Number.isFinite(idx) ? idx : null;
      }
      cur = cur.parentElement;
    }
    return null;
  };

  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    moved.current = false;
    onStart && onStart(index);
  }, [index, onStart]);

  const onTouchMove = useCallback((e) => {
    if (startX.current == null) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - startX.current);
    const dy = Math.abs(t.clientY - startY.current);
    if (dx > 6 || dy > 6) {
      moved.current = true;
      // prevent the page from scrolling while a drag is active
      try { e.preventDefault(); } catch {}
    }
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (!moved.current) {
      onEnd && onEnd();
      startX.current = null; startY.current = null; return;
    }
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) { onEnd && onEnd(); return; }
    const dropIdx = findTargetIndex(t.clientX, t.clientY);
    if (dropIdx != null && dropIdx !== index) {
      onReorder(index, dropIdx);
    }
    moved.current = false;
    startX.current = null; startY.current = null;
    onEnd && onEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, onReorder, onEnd]);

  return {
    "data-drag-type": dragType,
    "data-drag-index": index,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    style: { touchAction: "pan-y" },
  };
}
